import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import firebaseAuthLogger from "./firebaseAuthLogger";

interface RegisterData {
  email: string;
  password: string;
  first_name?: string;
}

interface UserDetails {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL?: string | null;
  firstName?: string;
  lastName?: string;
  createdDtm?: firebase.firestore.Timestamp;
  lastLoginTime?: firebase.firestore.Timestamp;
}

class FirebaseAuthBackend {
  private db: firebase.firestore.Firestore;

  constructor(firebaseConfig: any) {
    if (firebaseConfig) {
      // Initialize Firebase
      if (!firebase.apps.length) {
        firebase.initializeApp(firebaseConfig);
      }
      this.db = firebase.firestore();

      firebase.auth().onAuthStateChanged(async (user) => {
        if (user) {
          try {
            // Check if account document exists
            const accountDoc = await this.db
              .collection("accounts")
              .doc(user.uid)
              .get();

            if (!accountDoc.exists) {
              // Create account document for new users
              console.log("Creating account document for new user:", user.uid);
              await this.db
                .collection("accounts")
                .doc(user.uid)
                .set({
                  uid: user.uid,
                  email: user.email,
                  displayName: user.displayName || user.email?.split("@")[0] || "",
                  firstName: user.displayName || "",
                  createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                  lastLoginTime: firebase.firestore.FieldValue.serverTimestamp(),
                  emailVerified: user.emailVerified,
                });
              console.log("Account document created successfully");
            } else {
              console.log("Account document already exists for user:", user.uid);
            }

            const accountData = accountDoc.exists ? accountDoc.data() : null;

            // Combine auth user with account data
            const userData = {
              ...user,
              ...accountData,
            };

            sessionStorage.setItem("authUser", JSON.stringify(userData));
          } catch (error) {
            console.error("Error handling user auth state:", error);
            // Still store the user even if Firestore fails
            sessionStorage.setItem("authUser", JSON.stringify(user));
          }
        } else {
          sessionStorage.removeItem("authUser");
        }
      });
    } else {
      throw new Error("Firebase config is required");
    }
  }

  /**
   * Registers the user with given details - Simplified approach
   */
  registerUser = async (
    email: string,
    password: string,
    firstName?: string
  ) => {
    try {
      console.log("🔍 Firebase registration attempt for:", email);
      
      // Create auth user first
      const userCredential = await firebase
        .auth()
        .createUserWithEmailAndPassword(email, password);
  
      if (!userCredential.user) {
        await firebaseAuthLogger.logRegistrationFailure(email, 'Failed to create user', 'email');
        throw new Error("Failed to create user");
      }
  
      console.log("✅ Firebase registration successful for:", email);
  
      // Log successful registration
      await firebaseAuthLogger.logRegistration(userCredential.user, 'email');
  
      // Update profile
      if (firstName) {
        await userCredential.user.updateProfile({
          displayName: firstName,
        });
        console.log("Profile updated with display name");
      }
  
      // Return the user immediately - the onAuthStateChanged listener will handle Firestore
      console.log("Registration completed, onAuthStateChanged will handle account document");
      return userCredential.user;
    } catch (error: any) {
      console.error("❌ Firebase registration error:", error);
      await firebaseAuthLogger.logRegistrationFailure(email, this._handleError(error), 'email');
      return Promise.reject(this._handleError(error));
    }
  };
  /**
   * Login user with given details - FIXED VERSION
   */
  loginUser = async (email: string, password: string) => {
    try {
      console.log('🔍 Firebase login attempt for:', email);
      
      // First authenticate with Firebase Auth
      const userCredential = await firebase
        .auth()
        .signInWithEmailAndPassword(email, password);
  
      if (!userCredential.user) {
        // Log failed login
        await firebaseAuthLogger.logLoginFailure(email, 'Login failed - no user returned', 'email');
        throw new Error("Login failed");
      }
  
      console.log('✅ Firebase login successful for:', email);
  
      // Log successful login
      await firebaseAuthLogger.logLogin(userCredential.user, 'email');
  
      // After successful authentication, get user data from Firestore
      const userDoc = await this.db
        .collection("accounts")
        .doc(userCredential.user.uid)
        .get();
  
      let userData;
      if (userDoc.exists) {
        // Combine auth user with account data
        userData = {
          ...userCredential.user,
          ...userDoc.data(),
        };
  
        // Update last login time
        await this.db
          .collection("accounts")
          .doc(userCredential.user.uid)
          .update({
            lastLoginTime: firebase.firestore.FieldValue.serverTimestamp(),
          });
      } else {
        // If no account document exists, use auth user data
        userData = userCredential.user;
      }
  
      // Update session storage
      this.setLoggeedInUser(userData);
  
      return userCredential.user;
    } catch (error) {
      console.log('❌ Firebase login failed for:', email, error);
      await firebaseAuthLogger.logLoginFailure(email, this._handleError(error), 'email');
      throw this._handleError(error);
    }
  };

  /**
   * Forget Password user with given details
   */
  forgetPassword = (email: string) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .sendPasswordResetEmail(email, {
          url:
            window.location.protocol + "//" + window.location.host + "/login",
        })
        .then(async () => {
          // Log successful password reset request
          await firebaseAuthLogger.logPasswordReset(email, true);
          resolve(true);
        })
        .catch(async (error) => {
          // Log failed password reset request
          await firebaseAuthLogger.logPasswordReset(email, false, this._handleError(error));
          reject(this._handleError(error));
        });
    });
  };

  /**
   * Logout the user
   */
  logout = async () => {
    try {
      const currentUser = firebase.auth().currentUser;
      
      await firebase.auth().signOut();
      
      // Log logout
      if (currentUser) {
        await firebaseAuthLogger.logLogout(currentUser);
      }
      
      sessionStorage.removeItem("authUser");
      sessionStorage.removeItem("impersonatedUser");
      return true;
    } catch (error) {
      throw this._handleError(error);
    }
  };

  /**
   * Social Login user with given details
   */
  socialLoginUser = async (
    type: "google" | "facebook" | "apple" | "microsoft"
  ) => {
    let provider: firebase.auth.AuthProvider;
  
    switch (type) {
      case "google":
        provider = new firebase.auth.GoogleAuthProvider();
        break;
      case "facebook":
        provider = new firebase.auth.FacebookAuthProvider();
        break;
      case "apple":
        provider = new firebase.auth.OAuthProvider("apple.com");
        break;
      case "microsoft": {
        const microsoftProvider = new firebase.auth.OAuthProvider(
          "microsoft.com"
        );
  
        // Configure the Microsoft provider
        microsoftProvider.addScope("user.read");
  
        microsoftProvider.setCustomParameters({
          prompt: "select_account",
        });
  
        provider = microsoftProvider;
        break;
      }
      default:
        throw new Error(`Invalid social login type: ${type}`);
    }
  
    try {
      console.log('🔍 Firebase social login attempt:', type);
      
      const result = await firebase.auth().signInWithPopup(provider);
  
      if (!result.user || !result.additionalUserInfo) {
        await firebaseAuthLogger.logSocialLoginFailure('Social login failed - no user returned', type);
        throw new Error("Social login failed");
      }
  
      console.log('✅ Firebase social login successful:', type, result.user.email);
  
      // Log successful social login
      await firebaseAuthLogger.logSocialLogin(
        result.user, 
        type, 
        result.additionalUserInfo.isNewUser || false
      );
  
      await this.addNewUserToFirestore(result);
      return result.user;
    } catch (error) {
      console.error("❌ Firebase social login error:", type, error);
      await firebaseAuthLogger.logSocialLoginFailure(this._handleError(error), type);
      throw this._handleError(error);
    }
  };

  /**
   * Add new user to Firestore
   */
  addNewUserToFirestore = async (authResult: firebase.auth.UserCredential) => {
    if (!authResult.user || !authResult.additionalUserInfo?.profile) {
      throw new Error("Invalid auth result");
    }

    const {
      user,
      additionalUserInfo: { profile },
    } = authResult;
    const collection = this.db.collection("accounts");

    const details: UserDetails = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      firstName: (profile as any).given_name || (profile as any).first_name,
      lastName: (profile as any).family_name || (profile as any).last_name,
      createdDtm:
        firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
      lastLoginTime:
        firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
    };

    await collection.doc(user.uid).set(details, { merge: true });
    return { user, details };
  };

  setLoggeedInUser = (user: any) => {
    sessionStorage.setItem("authUser", JSON.stringify(user));
  };

  /**
   * Returns the authenticated or impersonated user
   */
  getAuthenticatedUser = () => {
    const impersonatedUser = sessionStorage.getItem("impersonatedUser");
    if (impersonatedUser) {
      return JSON.parse(impersonatedUser);
    }
    const authUser = sessionStorage.getItem("authUser");
    if (!authUser) return null;
    return JSON.parse(authUser);
  };

  /**
   * Handle the error
   */
  _handleError(error: any) {
    let errorMessage: string;

    if (typeof error === "string") {
      return error;
    }

    switch (error.code) {
      case "auth/email-already-in-use":
        errorMessage =
          "This email address is already registered. Please use a different email or try logging in.";
        break;
      case "auth/invalid-email":
        errorMessage = "Please enter a valid email address.";
        break;
      case "auth/operation-not-allowed":
        errorMessage = "Email/password registration is not enabled.";
        break;
      case "auth/weak-password":
        errorMessage =
          "Please choose a stronger password. It should be at least 6 characters long.";
        break;
      case "auth/user-not-found":
        errorMessage = "No account found with this email address.";
        break;
      case "auth/wrong-password":
        errorMessage = "Incorrect password. Please try again.";
        break;
      case "auth/invalid-credential":
        errorMessage = "Invalid email or password. Please check your credentials and try again.";
        break;
      case "auth/too-many-requests":
        errorMessage = "Too many failed login attempts. Please try again later.";
        break;
      default:
        errorMessage =
          error.message || "An error occurred during authentication.";
    }

    return errorMessage;
  }
}

let _fireBaseBackend: FirebaseAuthBackend | null = null;

/**
 * Initialize the backend
 * @param {*} config
 */
const initFirebaseBackend = (config: any): FirebaseAuthBackend => {
  if (!_fireBaseBackend) {
    _fireBaseBackend = new FirebaseAuthBackend(config);
  }
  return _fireBaseBackend;
};

/**
 * Returns the firebase backend
 */
const getFirebaseBackend = (): FirebaseAuthBackend | null => {
  return _fireBaseBackend;
};

export { initFirebaseBackend, getFirebaseBackend, FirebaseAuthBackend };